import React, {useCallback, useEffect} from "react";
import {
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    UncontrolledButtonDropdown,
    DropdownToggle,
    DropdownMenu, DropdownItem
} from 'reactstrap';
import "react-toastify/dist/ReactToastify.css";
import RightModal from "./modalForm";

// hooks
import { useRedux, useHistory, useFareRestriction, useSearchInput } from "../../../hooks";

import {
    editDataSaleCategory,
    getSaleCategories,
    onRemoveSaleCategory,
    removeSaleCategoryModalHide,
    removeSaleCategoryModalShow,
    showRightModalSaleCategory
} from "../../../redux/actions";

import TableList from "../../../components/TableList";
import SaleCategoryColumns from "./tableColumns";
import {SaleCategoryType} from "../../../redux/Sale/Category/types";
import {EntityActionsHandlersType} from "../../../redux/types";
import RemoveModal from "../../../components/RemoveForm";

import '../../../i18/config';
import {useTranslation} from "react-i18next";
import BreadCrumb from "../../../components/Common/BreadCrumb";
import Loader from "../../../components/Loader";
import * as route from "../../../api/routes";
import {LicenseProductParam} from "../../../redux/Sale/License/types";
import UpgradeFarePage from "../../../components/UpgradeFarePage";
import MainListFilter from "../../../components/MainListFilter";

const SaleCategoryIndex = () => {
    const { dispatch, useAppSelector } = useRedux();

    const { t } = useTranslation(['common']);

    const componentType = 'saleCategoryTable';

    const {
        historyHash,
        searchPhrase,
        loadItems,
        handleSearchClick
    } = useHistory({
        componentType,
        loadAction: getSaleCategories,
        dispatch
    });

    const { onSearchInput } = useSearchInput(handleSearchClick);

    const {
        saleCategory,
        isFetched,
        salePlan
    } = useAppSelector(state => ({
        saleCategory: state.SaleCategory,
        isFetched: state.SaleCategory.isFetched,
        salePlan: state.SalePlan
    }));

    const { upgradeFare } = useFareRestriction(
        salePlan?.currentPlan,
        LicenseProductParam.SALE_MAX_PRODUCTS
    );

    const openRightModal = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(editDataSaleCategory({data:event, modalEditTitle: 'changeCategory'}));
        } else {
            dispatch(showRightModalSaleCategory({data:[], modalEditTitle: 'newCategory'}));
        }
    },[dispatch]);

    const onEntityRemoveModalShow = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(removeSaleCategoryModalShow({data:event, modalEditTitle: 'deleteCategory'}));
        }
    },[dispatch]);

    const onEntityRemoveModalHide = useCallback(() => dispatch(removeSaleCategoryModalHide()),[dispatch]);

    const onEntityRemove = useCallback((event:any) => {
        if ((event !== undefined) && (event.uuid !== undefined) && (event.uuid.length>0)){
            dispatch(onRemoveSaleCategory({data:event, modalEditTitle: 'deleteCategory'}));
        }
    },[dispatch]);

    useEffect(() => {
        if (saleCategory.isNeedReload){
            loadItems();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadItems, saleCategory.isNeedReload]);

    const EntityActionsHandlers:EntityActionsHandlersType = {
        onEdit: openRightModal,
        onRemove: onEntityRemoveModalShow
    }

    const onTableRowClick = useCallback((item:SaleCategoryType) => {
        if (item.actions && item.actions.canEdit){
            openRightModal(item);
        }
    },[openRightModal]);

    const columns = SaleCategoryColumns(EntityActionsHandlers);

    return (
        <>
            <BreadCrumb title={t('menu:saleCategories')} pageTitle={t('menu:main')} />
            { upgradeFare && isFetched ?
                <>
                    <div className="d-flex flex-row flex-grow-1">
                        <div className="alert alert-danger alert-border-left alert-dismissible fade show d-flex flex-row flex-grow-1" role="alert">
                            <i className="ri-error-warning-line me-2 align-middle"></i> <strong className="pe-2">{t('common:attention')}!</strong>{t('plan:restrictionProductsText')}
                        </div>
                    </div>
                    <UpgradeFarePage/>
                </>
                :
                <>
                <Row className="g-4 mb-3">
                    <div className="col-sm-auto">
                        <UncontrolledButtonDropdown id="btnGroupVerticalDrop1">
                            <DropdownToggle color="success" caret>
                                <i className="ri-add-line align-bottom me-1 font-weight-normal"/>
                                {t('common:addNew')}
                            </DropdownToggle>
                            <DropdownMenu>
                                <DropdownItem href={route.SALE_PRODUCT_DETAIL_EDIT.replace(/:uuid/,'new')}>
                                    {t('product:product')}
                                </DropdownItem>
                                <DropdownItem onClick={openRightModal}>{t('category:category')}</DropdownItem>
                            </DropdownMenu>
                        </UncontrolledButtonDropdown>
                    </div>
                </Row>

                <Row>
                    <Col lg={12}>
                        <Card>
                            <CardHeader className="border-bottom-0">
                                <h5 className="card-title mb-0">{t('menu:saleCategories')}</h5>
                            </CardHeader>
                            <CardBody className="border border-dashed border-end-0 border-start-0 pb-3">
                                <MainListFilter
                                    onClick={handleSearchClick}
                                    value={searchPhrase}
                                    onChange={onSearchInput}
                                />
                            </CardBody>

                            <CardBody>
                                {isFetched ?
                                    <TableList
                                    columns={columns}
                                    data={saleCategory.items}
                                    onRowClick={onTableRowClick}
                                    key={componentType}
                                    historyHash = {historyHash}
                                    paginationTotalRows={saleCategory?.meta?.totalCount || 0}
                                    paginationServer={true}
                                    isShowChangePageSize={true}
                                    />
                                    :
                                    <Loader/>
                                }
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
                <RightModal/>
                <RemoveModal
                    onRemove={onEntityRemove}
                    handleClose={onEntityRemoveModalHide}
                    removeItem={saleCategory.selectedItem}
                    isOpen={saleCategory.removeModalShow}
                    modalTitle={saleCategory.modalEditTitle}/>
                </>
            }
        </>
    );
};

export default SaleCategoryIndex;


